import React from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { BannerAlert } from "@/components/wpl/banner-alert/src/banner-alert.tsx";
import { DemoAddOn, QuestionsCta } from "@/components/wpl/pricing/src";
import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { PricingFaq } from "@/components/wpl/pricing/src/pricing-faq/pricing-faq";
import { QuotationsAdornment } from "@/components/wpl/pricing/src/pricing-faq/quotations.tsx";
import {
    PricingPageLayout,
    PageHeading
} from "@/components/wpl/pricing/src/pricing-page-layout";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { BannerProps } from "@/patterns/adg4/banner-alert/banner-alert.tsx";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { CallToAction } from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import { SoftwareGuardPricingPageProps } from "../../../../../../pages/software/guard/pricing/software-guard-pricing-page.tsx";
import { GuardNav } from "../../../src/sections/guard-navbar.tsx";

import GuardPriceFinder from "./sections/guard-price-finder.tsx";

const inlineLink = css({
    "textDecoration": "underline",
    "color": wacToken("color.primary.blue"),
    "&:hover": {
        textDecoration: "none"
    }
});

const WebinarLink: React.FC<{ href: string; children: React.ReactNode }> = ({
    href,
    children
}) => (
    <a
        css={inlineLink}
        data-event="clicked"
        data-event-component="linkButton"
        data-product-key="guard"
        data-track-event="GuardPricingPage.webinar.banner"
        data-event-container="GuardPricingPageWebinar"
        data-label-english="Ask the Experts: Guard Premium AMA"
        href={href}
    >
        {children}
    </a>
);

const createWebinarLinkRenderer =
    (href: string) => (textstring: React.ReactNode) =>
        <WebinarLink href={href}>{textstring}</WebinarLink>;

export const SoftwareGuardPricingPageTemplate: React.FC<
    SoftwareGuardPricingPageProps
    // eslint-disable-next-line complexity
> = (props: SoftwareGuardPricingPageProps) => {
    const content = usePricingContentContext();
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;
    const addonCtaContent = content?.callToActionContainer?.[1] as CallToAction;
    const intl = useIntl();

    const localizedHref = useLocalizedHref(
        "/webinars/enterprise-cloud/defend-your-cloud-level-up-security-with-guard-premium"
    );

    const workshopHref = useLocalizedHref(
        "/webinars/enterprise-cloud/getting-started-with-guard-a-practical-walkthrough-of-security-best-practices"
    );

    const webinarLinkRenderer = React.useMemo(
        () => createWebinarLinkRenderer(localizedHref),
        [localizedHref]
    );

    const guardBannerProps: BannerProps = {
        identifier: "GuardPricingPage.banner-alert.premium-edition",
        productKey: "atlassianGuard",
        text: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.text",
            defaultMessage: "Atlassian Guard Premium is generally available!"
        }),
        linkText: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.link-text",
            defaultMessage: "Read the blog."
        }),
        linkUrl: useLocalizedHref("/blog/announcements/guard-premium"),
        dataEventProps: {
            "data-product-key": "atlassianGuard",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Read the blog",
            "data-label-english": "Read the blog"
        }
    };

    const webinarBannerProps: BannerProps = {
        identifier: "GuardPricingPage.banner-alert.webinar-september-4-2025",
        productKey: "atlassianGuard",
        text: (
            <FormattedMessage
                id="GuardPricingPage.banner-alert.text.webinar-september-4-2025"
                defaultMessage="<a>Ask the Experts: Guard Premium AMA</a> | Live Q&A | Sept 3rd & 4th"
                values={{
                    ...DEFAULT_VALUES,
                    a: webinarLinkRenderer
                }}
            />
        ),
        linkText: "",
        linkUrl: useLocalizedHref("/blog/announcements/guard-premium")
    };

    const workshopBannerProps: BannerProps = {
        identifier: "GuardPricingPage.banner-alert.guard-workshop",
        productKey: "atlassianGuard",
        text: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.text.guard-workshop",
            defaultMessage: "Get actionable tips to maximize Guard."
        }),
        linkText: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.link-text.guard-workshop",
            defaultMessage: "Register for the workshop now"
        }),
        linkUrl: workshopHref,
        dataEventProps: {
            "data-product-key": "atlassianGuard",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Register for the workshop now",
            "data-label-english": "Register for the workshop now"
        }
    };

    const displayWorkshopBanner = checkFeatureFlagGate(
        featureFlags.gwp8240ButterbarGuard
    );
    const displayWebinarBanner = checkFeatureFlagGate(
        featureFlags.gwp7735GuardWebinar
    );

    const displayedBannerProps = displayWorkshopBanner
        ? workshopBannerProps
        : displayWebinarBanner
        ? webinarBannerProps
        : guardBannerProps;

    return (
        <>
            <PricingPageLayout
                nav={<GuardNav />}
                banner={<BannerAlert {...displayedBannerProps} />}
                showFallback={content?.hasError}
            >
                <ParentScrollTracking
                    scrollTrackingEnabled={true}
                    pageId="guard-pricing-page"
                >
                    <PageHeading>{content?.headline}</PageHeading>
                    <GuardPriceFinder
                        pricing={props.pricing}
                        countryCode={props.countryCode}
                    />
                    <PricingFeatureTable
                        title={content?.compareFeaturesTitle ?? undefined}
                        comparePlans={content?.comparePlans ?? undefined}
                        compareFeatures={content?.compareFeatures ?? undefined}
                    />
                    <DemoAddOn
                        headingText={addonCtaContent?.title}
                        bodyText={addonCtaContent?.valueProposition}
                        linkText={addonCtaContent?.text}
                        ctaHref={addonCtaContent?.url ?? ""}
                    />
                    <QuotationsAdornment>
                        <ParentScrollTracking>
                            <PricingFaq
                                header={
                                    content?.getMoreDetailsTitle ?? undefined
                                }
                                planDetails={
                                    content?.additionalDetails ?? undefined
                                }
                            />
                        </ParentScrollTracking>
                    </QuotationsAdornment>
                    <QuestionsCta
                        product={"Guard"}
                        ctaHref={questionsCtaContent?.url ?? undefined}
                        questionsHeader={
                            questionsCtaContent?.title ?? undefined
                        }
                        priceAndLicensingLinkText={
                            questionsCtaContent?.text ?? undefined
                        }
                        questionsBody={
                            questionsCtaContent?.valueProposition ?? undefined
                        }
                        imageHref={questionsCtaContent?.asset?.url}
                    />
                </ParentScrollTracking>
            </PricingPageLayout>
        </>
    );
};
